import Table from "@/ui/table";
import { Index, Outflow, Price, Time, Value } from "@/ui/formats";
import { secondsAgoFromTimestamp } from "@/helpers";
import { IconComponent } from "@/ui/icon";
import Avatar from "@/ui/avatar";
import LoadingIndicator from "@/ui/loading-indicator";
import Pagination from "../pagination/pagination";
import { useEffect, useRef } from "react";
import { useTransactionsFilterContext } from "@/providers/transactions-filter/transactions-filter";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useWallet } from "@/providers/wallet";
import { useGetBotTransactions } from "@/api/transactions/transactions-bot";

const TRANSACTIONS_PER_PAGE = 100;

const TransactionsBotTableData = () => {
  const { currentWallet } = useWallet();

  const { page, setPage, filters, search, sort } =
    useTransactionsFilterContext();

  const searchQuery = useDebouncedValue(search, 300);

  const { data, isLoading, isError } = useGetBotTransactions(
    currentWallet,
    page,
    filters,
    searchQuery,
    sort
  );

  return (
    <>
      {isLoading ? (
        <Table.Row>
          <Table.Column className="!p-0" colSpan={7}>
            <LoadingIndicator />
          </Table.Column>
        </Table.Row>
      ) : isError ? (
        <Table.Row>
          <Table.Column colSpan={7} className="!p-[0.625rem]">
            Something went wrong while loading your wallet transactions. Please
            try again later.
          </Table.Column>
        </Table.Row>
      ) : data?.transactions.length === 0 ? (
        <Table.Row>
          <Table.Column colSpan={7} className="!p-[0.625rem]">
            <span>No transactions found for this wallet.</span>
          </Table.Column>
        </Table.Row>
      ) : (
        data?.transactions.map((item, index) => {
          const textClass = item.isBuy ? "text-green2" : "text-red2";

          return (
            <Table.Row
              key={item.txHash + index}
              className="h-[1.875rem] relative gradient-box before:py-[0.0625rem] before:hover:bg-gradient-100 hover:bg-table-hover"
            >
              <Table.Column className="w-[1.875rem] h-[1.875rem] flex justify-center items-center">
                <Index value={(page - 1) * TRANSACTIONS_PER_PAGE + index + 1} />
              </Table.Column>
              <Table.Column>
                <div className="flex items-center gap-x-[0.3125rem]">
                  <IconComponent
                    name={currentWallet.walletType}
                    className="w-[0.9375rem] h-[0.9375rem] shrink-0"
                  />
                  <div className="flex-row flex gap-x-[0.3125rem] items-center text-xs">
                    <div className="shrink-0">
                      <Avatar source={item.tokenImage} />
                    </div>
                    <p className="font-semibold capitalize text-white">
                      {item.tokenSymbol}
                    </p>
                  </div>
                </div>
              </Table.Column>
              <Table.Column className={textClass}>
                {item.isBuy ? "Buy" : "Sell"}
              </Table.Column>
              <Table.Column>
                <Price value={item.tokenPrice} decimals={7} />
              </Table.Column>
              <Table.Column>
                <div className="flex items-center gap-1.5">
                  <Avatar size="small" source={item.tokenImage} />
                  <Outflow value={item.amountFormatted} />
                </div>
              </Table.Column>
              <Table.Column>
                <Value value={item.usdValue} withSign={false} />
              </Table.Column>
              <Table.Column>
                <Time value={secondsAgoFromTimestamp(item.timestamp)} />
              </Table.Column>
            </Table.Row>
          );
        })
      )}
      {!isLoading &&
        data?.transactions &&
        (data.page > 1 || data.hasNextPage) && (
          <Table.Row className="w-full h-[1.5rem] text-xs mt-[auto]">
            <Table.Column colSpan={7}>
              <Pagination
                page={data.page}
                perPage={TRANSACTIONS_PER_PAGE}
                hasNextPage={data.hasNextPage}
                hasPreviousPage={data.page > 1}
                onNextPage={() => setPage(page + 1)}
                onPreviousPage={() => setPage(Math.max(page - 1, 1))}
                label="Transactions"
              />
            </Table.Column>
          </Table.Row>
        )}
    </>
  );
};

const TransactionsBotTable = () => {
  const { currentWallet } = useWallet();

  const { page, sort, handleSortChange } = useTransactionsFilterContext();

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  return (
    <Table ref={tableRef} className="text-left text-xs h-full pb-16">
      <colgroup>
        <col />
        <col style={{ width: "22%" }} />
      </colgroup>
      <Table.Header className="h-[1.5625rem] bg-black sticky top-0 z-10">
        <Table.HeaderColumn className="!pl-[0.625rem] w-[1.5625rem]">
          #
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("token")}
          >
            Token
            {sort.sortField === "token" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn className="!pl-0">
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("type")}
          >
            Type
            {sort.sortField === "type" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>Price</Table.HeaderColumn>
        <Table.HeaderColumn>Amount</Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("value")}
          >
            Value
            {sort.sortField === "value" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("timestamp")}
          >
            Time
            {sort.sortField === "timestamp" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
      </Table.Header>
      <Table.Body>
        {!currentWallet ? (
          <Table.Row>
            <Table.Column colSpan={7} className="!p-[0.625rem]">
              Sign in to view data
            </Table.Column>
          </Table.Row>
        ) : (
          <TransactionsBotTableData />
        )}
      </Table.Body>
    </Table>
  );
};

export default TransactionsBotTable;
